/**
 * Script loading and initialization utilities
 */

interface ScriptLoadOptions {
  url?: string;
  async?: boolean;
  defer?: boolean;
  crossorigin?: string;
  integrity?: string;
}

/**
 * Load a script dynamically
 */
export function loadScript(options: ScriptLoadOptions = {}): Promise<void> {
  return new Promise((resolve, reject) => {
    const {
      url = 'https://cdn.fanbasis.com/checkout/latest.js',
      async = true,
      defer = false,
      crossorigin,
      integrity
    } = options;

    // Check if script is already loaded
    const existingScript = document.querySelector(`script[src="${url}"]`);
    if (existingScript) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = async;
    script.defer = defer;
    
    if (crossorigin) {
      script.crossOrigin = crossorigin;
    }
    
    if (integrity) {
      script.integrity = integrity;
    }

    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${url}`));
    
    document.head.appendChild(script);
  });
}

/**
 * Load checkout script with default configuration
 */
export function loadCheckoutScript(version?: string): Promise<void> {
  const url = version 
    ? `https://cdn.fanbasis.com/checkout/v${version}/checkout.js`
    : 'https://cdn.fanbasis.com/checkout/latest.js';
  
  return loadScript({ url });
}

/**
 * Initialize checkout when DOM is ready
 */
export function initializeOnReady(callback: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}

/**
 * Auto-initialize checkout elements
 */
export function autoInitializeCheckout(): void {
  const elements = document.querySelectorAll('[data-checkout]');
  
  elements.forEach((element) => {
    if (!(element as any).__checkoutInitialized) {
      try {
        // Import dynamically to avoid circular dependencies
        import('./attributeParser').then(({ autoInitialize }) => {
          autoInitialize();
        });
        (element as any).__checkoutInitialized = true;
      } catch (error) {
        console.error('Failed to initialize checkout element:', error);
      }
    }
  });
}

/**
 * Set up global checkout initialization
 */
export function setupGlobalInitialization(): void {
  // Auto-initialize on DOM ready
  initializeOnReady(() => {
    if (document.querySelector('[data-checkout-auto]')) {
      autoInitializeCheckout();
    }
  });

  // Auto-initialize on new elements (MutationObserver)
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            if (element.hasAttribute('data-checkout') || element.querySelector('[data-checkout]')) {
              autoInitializeCheckout();
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

/**
 * Check if checkout script is loaded
 */
export function isCheckoutLoaded(): boolean {
  return typeof window !== 'undefined' && 
         (window.PaymentCheckout !== undefined || window.FanbasisCheckout !== undefined);
}

/**
 * Wait for checkout to be available
 */
export function waitForCheckout(timeout = 10000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (isCheckoutLoaded()) {
      resolve();
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (isCheckoutLoaded()) {
        clearInterval(checkInterval);
        resolve();
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        reject(new Error('Checkout failed to load within timeout'));
      }
    }, 100);
  });
} 